import { Fragment } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { SrIcon } from "@/components/ui/SrIcon";
import { Avatar } from "@/components/shared/Avatar";
import { cn } from "@/lib/utils";
import { formatDate, formatNOK } from "@/lib/format";
import {
  defaultSortDirection,
  groupByPrimaryDiscipline,
  isTested,
  ratingSummary,
  sortFreelancers,
  type FreelancerListRow,
  type FreelancerSortKey,
  type SortDirection,
} from "@/lib/freelancers";
import { RatingStars } from "./RatingStars";

export interface FreelancerSort {
  key: FreelancerSortKey;
  dir: SortDirection;
}

interface Props {
  rows: FreelancerListRow[];
  /** null = gruppert på hovedfagfelt (standard). */
  sort: FreelancerSort | null;
  onSortChange: (sort: FreelancerSort | null) => void;
  emptyMessage: string;
  /** VIB-2927: «Sist brukt»-kolonnen, når frilans på prosjekt er på. */
  showLastUsed?: boolean;
}

type Column = { key: FreelancerSortKey | null; label: string; align?: "right" };

const COLUMNS: Column[] = [
  { key: "name", label: "Navn" },
  { key: null, label: "Fagfelt" },
  { key: "rating", label: "Vurdering" },
  { key: "day_rate", label: "Dagspris", align: "right" },
  { key: "location", label: "Sted" },
  { key: null, label: "Kontakt" },
];
const LAST_USED_COLUMN: Column = { key: "last_used", label: "Sist brukt" };

export function RatingCell({ row }: { row: FreelancerListRow }) {
  if (!isTested(row)) return <Badge variant="outline" className="text-xs">Ikke testet</Badge>;
  if (row.ratings.length === 0) return <span className="text-xs text-muted-foreground">Brukt, ikke vurdert</span>;
  const { avg } = ratingSummary(row.ratings);
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <RatingStars value={avg} />
      <span className="text-xs text-muted-foreground tabular-nums">{avg?.toLocaleString("nb-NO")}</span>
    </span>
  );
}

function subline(row: FreelancerListRow): string {
  return [row.company_name, row.location].filter(Boolean).join(" · ");
}

function NameCell({ row }: { row: FreelancerListRow }) {
  return (
    <div className="flex items-center gap-3">
      <Avatar name={row.name} size="md" className={cn("shrink-0", row.kind === "company" && "rounded-md")} />
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          <Link to={`/frilansere/${row.id}`} className="font-medium text-primary hover:underline">{row.name}</Link>
          {row.kind === "company" && <Badge variant="outline" className="px-2 text-[11px]">Firma</Badge>}
          {row.archived_at && <Badge variant="secondary" className="px-2 text-[11px]">Arkivert</Badge>}
        </div>
        {subline(row) && <div className="truncate text-xs text-muted-foreground">{subline(row)}</div>}
      </div>
    </div>
  );
}

function ContactIcons({ row }: { row: FreelancerListRow }) {
  return (
    <span className="flex items-center gap-2.5 text-muted-foreground">
      {row.phone && (
        <a href={`tel:${row.phone}`} aria-label={`Ring ${row.name}`} className="hover:text-foreground">
          <SrIcon name="phone" className="h-4 w-4" />
        </a>
      )}
      {row.email && (
        <a href={`mailto:${row.email}`} aria-label={`Send e-post til ${row.name}`} className="hover:text-foreground">
          <SrIcon name="mail" className="h-4 w-4" />
        </a>
      )}
    </span>
  );
}

function Row({ row, showLastUsed }: { row: FreelancerListRow; showLastUsed: boolean }) {
  return (
    <tr className={cn("border-b border-border/50 last:border-0 hover:bg-muted/30", row.archived_at && "opacity-60")}>
      <td className="px-3 py-2.5"><NameCell row={row} /></td>
      <td className="px-3 py-2.5">
        <div className="flex flex-wrap gap-1">
          {row.disciplines.map((d) => <Badge key={d.id} variant="secondary" className="px-2 text-xs">{d.label}</Badge>)}
        </div>
      </td>
      <td className="px-3 py-2.5"><RatingCell row={row} /></td>
      <td className="px-3 py-2.5 text-right tabular-nums font-medium">
        {row.day_rate_nok != null ? formatNOK(row.day_rate_nok) : <span className="font-normal text-muted-foreground">Ikke oppgitt</span>}
      </td>
      <td className="px-3 py-2.5">{row.location ?? <span className="text-muted-foreground">—</span>}</td>
      <td className="px-3 py-2.5"><ContactIcons row={row} /></td>
      {showLastUsed && (
        <td className="whitespace-nowrap px-3 py-2.5 text-muted-foreground">{row.last_used ? formatDate(row.last_used) : "—"}</td>
      )}
    </tr>
  );
}

function MobileCard({ row }: { row: FreelancerListRow }) {
  return (
    <Link to={`/frilansere/${row.id}`} className={cn("block border-[1.5px] border-foreground bg-card p-3", row.archived_at && "opacity-60")}>
      <div className="flex items-start justify-between gap-3">
        <NameCell row={row} />
        <span className="shrink-0 text-sm font-medium tabular-nums">{row.day_rate_nok != null ? `${formatNOK(row.day_rate_nok)}/dag` : ""}</span>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <RatingCell row={row} />
        {row.disciplines.map((d) => <Badge key={d.id} variant="secondary" className="px-2 text-xs">{d.label}</Badge>)}
      </div>
    </Link>
  );
}

export function FreelancerListTable({ rows, sort, onSortChange, emptyMessage, showLastUsed = false }: Props) {
  if (rows.length === 0) return <p className="p-4 text-sm text-muted-foreground">{emptyMessage}</p>;

  const columns = showLastUsed ? [...COLUMNS, LAST_USED_COLUMN] : COLUMNS;
  const groups = sort ? [{ key: "sorted", label: "", rows: sortFreelancers(rows, sort.key, sort.dir) }] : groupByPrimaryDiscipline(rows);

  const toggleSort = (key: FreelancerSortKey) => {
    if (sort?.key !== key) onSortChange({ key, dir: defaultSortDirection(key) });
    else if (sort.dir === defaultSortDirection(key)) onSortChange({ key, dir: sort.dir === "asc" ? "desc" : "asc" });
    else onSortChange(null);
  };

  return (
    <>
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              {columns.map((c) => (
                <th
                  key={c.label}
                  scope="col"
                  aria-sort={sort?.key === c.key ? (sort.dir === "asc" ? "ascending" : "descending") : undefined}
                  className={cn("whitespace-nowrap px-3 py-2 text-xs font-medium uppercase tracking-wider text-muted-foreground", c.align === "right" ? "text-right" : "text-left")}
                >
                  {c.key ? (
                    <button type="button" onClick={() => toggleSort(c.key!)} className="inline-flex items-center gap-1 uppercase hover:text-foreground">
                      {c.label}
                      {sort?.key === c.key && <SrIcon name={sort.dir === "asc" ? "arrow-up" : "arrow-down"} className="h-3 w-3" />}
                    </button>
                  ) : c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <Fragment key={g.key}>
                {g.label && (
                  <tr>
                    <td colSpan={columns.length} className="bg-muted/50 px-3 py-1.5 text-[11px] font-medium uppercase tracking-wider">
                      {g.label} · {g.rows.length}
                    </td>
                  </tr>
                )}
                {g.rows.map((row) => <Row key={row.id} row={row} showLastUsed={showLastUsed} />)}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-col gap-2 md:hidden">
        {groups.flatMap((g) => g.rows).map((row) => <MobileCard key={row.id} row={row} />)}
      </div>
    </>
  );
}
